import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Divider, FormControlLabel, IconButton, LinearProgress, List, ListItem, Menu, MenuItem, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/AddOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import HearingIcon from '@mui/icons-material/HearingOutlined';
import type { BrandAsset, BrandKit } from '../../../services/editingApi';
import type { EditList, Voiceover } from '../../../utils/editing/editList';
import { EDIT_LIMITS, outputDurationMs } from '../../../utils/editing/editList';
import { addVoiceover, formatMs, newItemId, removeItem, updateItem } from '../editStudioModel';
import type { MixLevels } from '../usePreviewAudio';
import { Choice, DbSlider, PanelHint, SecondsField } from './controls';

interface SoundPanelProps {
  list: EditList;
  kit: BrandKit | null;
  outputMs: number;
  onChange: (next: EditList, key?: string) => void;
  onOpenBrandKit: () => void;
  /** Spoken lines: the voices on offer (empty when TTS isn't available) and how to generate one. */
  voices?: string[];
  onSpeak?: (voiceoverId: string) => Promise<void>;
  /** A short sample of a voice, to choose by ear. */
  onSample?: (voice: string) => Promise<string>;
  /** Peak levels while playing (the meters); absent when they can't be read. */
  levels?: MixLevels | null;
}

/** "ava" → "Ava (HD)"; an old Workers AI name stays as it is, capitalised. */
const HD_VOICES = ['ava', 'andrew', 'emma', 'brian'];
export const voiceLabel = (voice: string): string => {
  const name = voice.charAt(0).toUpperCase() + voice.slice(1);
  return HD_VOICES.indexOf(voice) >= 0 ? `${name} (HD)` : name;
};
const preferredVoice = (voices: string[]): string => (voices.indexOf('ava') >= 0 ? 'ava' : voices[0]);

const Meter: React.FC<{ level: number | undefined; label: string }> = ({ level, label }) => (
  level === undefined ? null : (
    <Tooltip title={`${label} level while playing`}>
      <LinearProgress variant="determinate" value={Math.min(100, level * 100)} aria-label={`${label} level`} sx={{ height: 6, borderRadius: 3, mt: 0.25, '& .MuiLinearProgress-bar': { bgcolor: level > 0.95 ? 'error.main' : level > 0.7 ? 'warning.main' : 'success.main', transition: 'none' } }} />
    </Tooltip>
  )
);

/** The original sound, music under it, and voiceovers on top. */
const SoundPanel: React.FC<SoundPanelProps> = ({ list, kit, outputMs, onChange, onOpenBrandKit, voices = [], onSpeak, onSample, levels = null }) => {
  const [musicMenu, setMusicMenu] = useState<HTMLElement | null>(null);
  const [voiceMenu, setVoiceMenu] = useState<HTMLElement | null>(null);
  const [lineText, setLineText] = useState('');
  const [speaking, setSpeaking] = useState<string | null>(null);
  const [speakProblem, setSpeakProblem] = useState<string | null>(null);
  // Hearing one line (or a voice sample) on its own, apart from the preview.
  const [playing, setPlaying] = useState<string | null>(null);
  const player = useRef<HTMLAudioElement | null>(null);
  const stopLine = () => {
    player.current?.pause();
    player.current = null;
    setPlaying(null);
  };
  useEffect(() => () => { player.current?.pause(); }, []);
  const playUrl = (id: string, url: string, gainDb = 0) => {
    if (playing === id) { stopLine(); return; }
    player.current?.pause();
    const el = new Audio(url);
    el.volume = Math.min(1, Math.pow(10, gainDb / 20));
    el.onended = () => { if (player.current === el) { player.current = null; setPlaying(null); } };
    el.onerror = () => { if (player.current === el) { player.current = null; setPlaying(null); setSpeakProblem('That couldn’t be played.'); } };
    player.current = el;
    setPlaying(id);
    const started = el.play();
    if (started && typeof started.catch === 'function') started.catch(() => { if (player.current === el) { player.current = null; setPlaying(null); } });
  };
  const playLine = (voiceover: Voiceover, url: string) => playUrl(voiceover.id, url, voiceover.gainDb);
  const hearSample = async (voice: string) => {
    if (!onSample) return;
    const id = `sample-${voice}`;
    if (playing === id) { stopLine(); return; }
    setSpeakProblem(null);
    try {
      playUrl(id, await onSample(voice));
    } catch (error) {
      setSpeakProblem(error instanceof Error ? error.message : 'The sample couldn’t be played.');
    }
  };
  const addSpokenLine = () => {
    const text = lineText.trim();
    if (!text || voices.length === 0) return;
    const item: Voiceover = { id: newItemId('line'), source: { kind: 'tts', text, voice: preferredVoice(voices), assetId: null }, atMs: Math.max(0, Math.round(outputMs)), gainDb: 0, duckOriginal: true };
    onChange({ ...list, voiceovers: [...list.voiceovers, item] });
    setLineText('');
  };
  const speak = async (voiceoverId: string) => {
    if (!onSpeak) return;
    if (playing === voiceoverId) stopLine();
    setSpeaking(voiceoverId);
    setSpeakProblem(null);
    try {
      await onSpeak(voiceoverId);
    } catch (error) {
      setSpeakProblem(error instanceof Error ? error.message : 'The voice couldn’t be generated.');
    } finally {
      setSpeaking(null);
    }
  };
  const sounds: BrandAsset[] = (kit?.assets || []).filter((asset) => asset.kind === 'audio');
  const tracks = sounds.filter((asset) => !asset.generated);
  const urlOf = (assetId: string | null | undefined): string | null => (assetId ? sounds.find((asset) => asset.id === assetId)?.url || null : null);
  const total = outputDurationMs(list);
  const music = list.music;
  const originalMuted = list.audio.originalGainDb <= EDIT_LIMITS.gainDb.min;
  const name = (id: string | null) => sounds.find((asset) => asset.id === id)?.name || 'Sound';
  const changeMusic = (patch: Partial<NonNullable<EditList['music']>>, key?: string) => { if (music) onChange({ ...list, music: { ...music, ...patch } }, key); };
  const setLine = (voiceover: Voiceover, change: Partial<Voiceover>, key?: string) => onChange({ ...list, voiceovers: updateItem(list.voiceovers, voiceover.id, change) }, key);

  return (
    <Box>
      <Typography variant="subtitle2">Original sound</Typography>
      <FormControlLabel
        control={<Switch checked={!originalMuted} onChange={(event) => onChange({ ...list, audio: { ...list.audio, originalGainDb: event.target.checked ? 0 : EDIT_LIMITS.gainDb.min } })} inputProps={{ 'aria-label': 'Original sound on' }} data-testid="original-sound" />}
        label={<Typography variant="body2">{originalMuted ? 'Muted' : 'On'}</Typography>}
      />
      {!originalMuted ? <DbSlider label="Level" value={list.audio.originalGainDb} min={-30} max={EDIT_LIMITS.gainDb.max} onChange={(originalGainDb) => onChange({ ...list, audio: { ...list.audio, originalGainDb } }, 'original-level')} /> : null}
      <Meter level={levels ? levels.original : undefined} label="Original sound" />
      <FormControlLabel control={<Switch size="small" checked={list.audio.normalize} onChange={(event) => onChange({ ...list, audio: { ...list.audio, normalize: event.target.checked } })} />} label={<Typography variant="body2">Even out the loudness at the end</Typography>} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>To silence only part of it, select a clip on the timeline and mute that stretch.</Typography>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2">Music</Typography>
      <PanelHint>A track from your brand kit under the whole result, lowered while people speak.</PanelHint>
      {music ? (
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ flex: 1 }} noWrap>{name(music.assetId)}</Typography>
            {urlOf(music.assetId) ? <IconButton size="small" aria-label={playing === 'music' ? 'Stop the track' : 'Play the track'} onClick={() => playUrl('music', urlOf(music.assetId) as string, music.gainDb)}>{playing === 'music' ? <StopIcon fontSize="inherit" /> : <PlayArrowIcon fontSize="inherit" />}</IconButton> : null}
            <IconButton size="small" aria-label="Remove music" onClick={() => onChange({ ...list, music: null })}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
          </Stack>
          <DbSlider label="Level" value={music.gainDb} min={-40} max={0} onChange={(gainDb) => changeMusic({ gainDb }, 'music-level')} />
          <Meter level={levels ? levels.music : undefined} label="Music" />
          <FormControlLabel control={<Switch checked={music.ducking} onChange={(event) => changeMusic({ ducking: event.target.checked })} />} label={<Typography variant="body2">Lower it while people speak</Typography>} />
          {music.ducking ? <DbSlider label="By" value={music.duckDb} min={EDIT_LIMITS.duckDb.min} max={EDIT_LIMITS.duckDb.max} onChange={(duckDb) => changeMusic({ duckDb }, 'music-duck')} /> : null}
          <FormControlLabel control={<Switch checked={music.loop} onChange={(event) => changeMusic({ loop: event.target.checked })} />} label={<Typography variant="body2">Repeat it to the end</Typography>} />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <SecondsField label="Starts at" valueMs={music.startMs} maxMs={total} onChange={(startMs) => { if (startMs !== null) changeMusic({ startMs }); }} />
            <SecondsField label="Skip intro" valueMs={music.inMs} maxMs={EDIT_LIMITS.maxSourceMs} onChange={(inMs) => { if (inMs !== null) changeMusic({ inMs }); }} testId="music-in" />
            <SecondsField label="Fade in" valueMs={music.fadeInMs} maxMs={EDIT_LIMITS.maxFadeMs} onChange={(fadeInMs) => { if (fadeInMs !== null) changeMusic({ fadeInMs }); }} />
            <SecondsField label="Fade out" valueMs={music.fadeOutMs} maxMs={EDIT_LIMITS.maxFadeMs} onChange={(fadeOutMs) => { if (fadeOutMs !== null) changeMusic({ fadeOutMs }); }} />
          </Stack>
          <Typography variant="caption" color="text.secondary">“Skip intro” starts the music that far into the file; repeats come back to there.</Typography>
        </Stack>
      ) : (
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={(event) => (tracks.length ? setMusicMenu(event.currentTarget) : onOpenBrandKit())} data-testid="add-music">
          {tracks.length ? 'Add music' : 'Upload music'}
        </Button>
      )}
      <Menu open={Boolean(musicMenu)} anchorEl={musicMenu} onClose={() => setMusicMenu(null)}>
        {tracks.map((asset) => (
          <MenuItem key={asset.id} onClick={() => { onChange({ ...list, music: { assetId: asset.id, startMs: 0, inMs: 0, gainDb: -14, ducking: true, duckDb: 12, fadeInMs: 1000, fadeOutMs: 2000, loop: true } }); setMusicMenu(null); }}>{asset.name}</MenuItem>
        ))}
        <MenuItem onClick={() => { setMusicMenu(null); onOpenBrandKit(); }}>Upload another…</MenuItem>
      </Menu>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2">Voiceover</Typography>
      <PanelHint>A recording from your brand kit placed at the playhead, or a line spoken by a natural voice.</PanelHint>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} disabled={list.voiceovers.length >= EDIT_LIMITS.maxVoiceovers} onClick={(event) => (tracks.length ? setVoiceMenu(event.currentTarget) : onOpenBrandKit())} data-testid="add-voiceover">
          {tracks.length ? 'Add a recording here' : 'Upload a recording'}
        </Button>
      </Stack>
      {voices.length > 0 ? (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField size="small" fullWidth label="A line to speak here" value={lineText} onChange={(event) => setLineText(event.target.value)} inputProps={{ maxLength: EDIT_LIMITS.maxTtsTextLength, 'data-testid': 'tts-text' }} multiline maxRows={3} />
          <Button size="small" variant="outlined" onClick={addSpokenLine} disabled={!lineText.trim() || list.voiceovers.length >= EDIT_LIMITS.maxVoiceovers} data-testid="add-tts">Add</Button>
        </Stack>
      ) : null}
      <Menu open={Boolean(voiceMenu)} anchorEl={voiceMenu} onClose={() => setVoiceMenu(null)}>
        {tracks.map((asset) => (
          <MenuItem key={asset.id} onClick={() => { onChange(addVoiceover(list, asset.id, outputMs)); setVoiceMenu(null); }}>{asset.name}</MenuItem>
        ))}
      </Menu>
      {speakProblem ? <Alert severity="error" sx={{ mt: 1 }} onClose={() => setSpeakProblem(null)}>{speakProblem}</Alert> : null}
      {list.voiceovers.length ? <Meter level={levels ? levels.voice : undefined} label="Voiceover" /> : null}
      <List dense disablePadding sx={{ mt: 1 }}>
        {list.voiceovers.map((voiceover) => {
          const url = urlOf(voiceover.source.assetId);
          const isPlaying = playing === voiceover.id;
          const tts = voiceover.source.kind === 'tts' ? voiceover.source : null;
          return (
            <ListItem key={voiceover.id} disableGutters sx={{ display: 'block', py: 1, borderTop: '1px solid', borderColor: 'divider' }} data-testid={`voiceover-${voiceover.id}`}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {tts ? (
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    maxRows={3}
                    label="Spoken line"
                    value={tts.text}
                    onChange={(event) => setLine(voiceover, { source: { ...tts, text: event.target.value, assetId: null } }, `line-text-${voiceover.id}`)}
                    inputProps={{ maxLength: EDIT_LIMITS.maxTtsTextLength, 'data-testid': `line-text-${voiceover.id}` }}
                  />
                ) : (
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap>{name(voiceover.source.assetId)}</Typography>
                )}
                <IconButton size="small" aria-label={isPlaying ? 'Stop this line' : 'Play this line'} disabled={!url} onClick={() => (url ? playLine(voiceover, url) : undefined)} data-testid={`play-${voiceover.id}`}>
                  {isPlaying ? <StopIcon fontSize="inherit" /> : <PlayArrowIcon fontSize="inherit" />}
                </IconButton>
                <IconButton size="small" aria-label="Remove voiceover" onClick={() => { if (playing === voiceover.id) stopLine(); onChange({ ...list, voiceovers: removeItem(list.voiceovers, voiceover.id) }); }}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
              </Stack>
              <Typography variant="caption" color="text.secondary">at {formatMs(voiceover.atMs)}{tts && !tts.assetId ? ' · not generated yet' : ''}</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <SecondsField label="At" valueMs={voiceover.atMs} maxMs={total} onChange={(atMs) => { if (atMs !== null) setLine(voiceover, { atMs }); }} />
                <Box sx={{ flex: 1 }}><DbSlider label="Level" value={voiceover.gainDb} min={-20} max={12} onChange={(gainDb) => setLine(voiceover, { gainDb }, `line-level-${voiceover.id}`)} /></Box>
              </Stack>
              <FormControlLabel control={<Switch size="small" checked={voiceover.duckOriginal} onChange={(event) => setLine(voiceover, { duckOriginal: event.target.checked })} />} label={<Typography variant="caption">Lower the original while it plays</Typography>} />
              {tts ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                  <Box sx={{ width: 160 }}>
                    <Choice label="Voice" value={tts.voice} options={(voices.includes(tts.voice) || !voices.length ? (voices.length ? voices : [tts.voice]) : [tts.voice, ...voices]).map((voice) => ({ value: voice, label: voiceLabel(voice) }))} onChange={(voice) => setLine(voiceover, { source: { ...tts, voice, assetId: null } })} />
                  </Box>
                  {onSample ? (
                    <Tooltip title="Hear this voice">
                      <IconButton size="small" aria-label={`Hear ${voiceLabel(tts.voice)}`} onClick={() => hearSample(tts.voice)} data-testid={`sample-${voiceover.id}`}>
                        {playing === `sample-${tts.voice}` ? <StopIcon fontSize="inherit" /> : <HearingIcon fontSize="inherit" />}
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  <Button size="small" variant={tts.assetId ? 'text' : 'contained'} disabled={!onSpeak || speaking !== null || !tts.text.trim()} onClick={() => speak(voiceover.id)} data-testid={`speak-${voiceover.id}`}>
                    {speaking === voiceover.id ? 'Generating…' : tts.assetId ? 'Generate again' : 'Generate the voice'}
                  </Button>
                </Stack>
              ) : null}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default SoundPanel;
